import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, MessageCircleHeart, ChevronLeft, ChevronRight } from 'lucide-react';

const TESTIMONIALS = [
  { id: 1, name: 'Rajesh Kumar', initials: 'RK', role: 'Driver', accent: 'teal',
    review: 'TotoWala helped me earn more as a driver. The commission is the lowest I have seen, my family is much happier now.',
    location: 'Bhagalpur' },
  { id: 2, name: 'Priya Singh', initials: 'PS', role: 'Rider', accent: 'primary',
    review: 'I like that I pay only the ride fare, no hidden charges. The app is simple to use and drivers are always professional.',
    location: 'Bhagalpur' },
  { id: 3, name: 'Amit Sharma', initials: 'AS', role: 'Rider', accent: 'primary',
    review: 'The booking process is super quick and hassle-free. I get my ride within minutes and the payment is transparent.',
    location: 'Bihar Sharif' },
  { id: 4, name: 'Sunita Devi', initials: 'SD', role: 'Driver', accent: 'teal',
    review: "Being a female driver, I feel safe with TotoWala's verification system. The direct payment feature has improved my income significantly.",
    location: 'Bhagalpur' },
  { id: 5, name: 'Vikash Singh', initials: 'VS', role: 'Rider', accent: 'primary',
    review: 'Great service with fair pricing. No surprise fees at the end of the ride.',
    location: 'Munger' },
];

const MARQUEE_ITEMS = [...TESTIMONIALS, ...TESTIMONIALS];

const COLOR_CLASSES = {
  teal:    { text: 'text-teal-dark dark:text-teal', solid: 'bg-teal-dark', tint: 'bg-teal-tint dark:bg-teal/10', border: 'border-teal/25', borderL: 'border-l-teal-dark dark:border-l-teal' },
  primary: { text: 'text-primary',                   solid: 'bg-primary',  tint: 'bg-primary-tint dark:bg-primary/10', border: 'border-primary/25', borderL: 'border-l-primary' },
};

const MarqueeCard = ({ t }) => {
  const c = COLOR_CLASSES[t.accent];
  return (
    <div className={`w-[300px] shrink-0 p-5 rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line border-l-[3px] ${c.borderL}`}>
      <div className={`flex gap-0.5 mb-3 ${c.text}`}>
        {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
      </div>
      <p className="text-sm text-ink-soft dark:text-dark-ink-soft leading-relaxed mb-4">"{t.review}"</p>
      <div className="flex items-center gap-2.5">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${c.solid}`}>
          {t.initials}
        </div>
        <div>
          <p className="text-sm font-bold text-ink dark:text-dark-ink leading-tight">{t.name}</p>
          <div className="flex items-center gap-1.5">
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.tint} ${c.text}`}>{t.role}</span>
            <span className="text-[11px] text-ink-soft">{t.location}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Testimonials = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((p) => (p + 1) % TESTIMONIALS.length), 4500);
    return () => clearInterval(id);
  }, []);

  const t = TESTIMONIALS[active];
  const c = COLOR_CLASSES[t.accent];

  return (
    <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4 overflow-hidden">
      <style>{`
        @keyframes tm-left { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .tm-track-left { animation: tm-left 35s linear infinite; }
        .tm-track-left:hover { animation-play-state: paused; }
      `}</style>

      <div className="max-w-[1200px] mx-auto">

        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4 bg-primary-tint dark:bg-primary/10 border border-primary/25 text-primary">
            <MessageCircleHeart size={13} />
            Community Voices
          </span>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight text-ink dark:text-dark-ink mb-3">
            Real people, <span className="text-primary">real stories.</span>
          </h2>
          <p className="text-base text-ink-soft dark:text-dark-ink-soft">
            Drivers keep more. Riders pay less.
          </p>
        </div>

        <div className="relative mb-10 overflow-hidden">
          <div className="absolute inset-y-0 left-0 w-16 z-10 pointer-events-none bg-gradient-to-r from-white dark:from-dark-bg to-transparent" />
          <div className="absolute inset-y-0 right-0 w-16 z-10 pointer-events-none bg-gradient-to-l from-white dark:from-dark-bg to-transparent" />
          <div className="tm-track-left flex gap-4" style={{ width: 'max-content' }}>
            {MARQUEE_ITEMS.map((item, i) => (
              <MarqueeCard key={`${item.id}-${i}`} t={item} />
            ))}
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="rounded-card bg-surface-alt dark:bg-dark-surface border border-line dark:border-dark-line p-8 sm:p-10 text-center">
            <div className={`flex gap-1 justify-center mb-5 ${c.text}`}>
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
            </div>

            <div className="min-h-[5.5rem] sm:min-h-[4.5rem] flex items-center justify-center mb-6">
              <AnimatePresence mode="wait">
                <motion.p
                  key={`quote-${active}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className="text-lg text-ink dark:text-dark-ink leading-relaxed"
                >
                  "{t.review}"
                </motion.p>
              </AnimatePresence>
            </div>

            <div className="w-10 h-px bg-line dark:bg-dark-line mx-auto mb-6" />

            <AnimatePresence mode="wait">
              <motion.div
                key={`author-${active}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="flex items-center justify-center gap-3"
              >
                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0 ${c.solid}`}>
                  {t.initials}
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-ink dark:text-dark-ink">{t.name}</p>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.tint} ${c.text}`}>{t.role}</span>
                    <span className="text-xs text-ink-soft">{t.location}</span>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setActive((p) => (p - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
              className="w-9 h-9 rounded-full border border-line dark:border-dark-line flex items-center justify-center"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={16} className="text-ink-muted" />
            </button>
            <div className="flex gap-1.5 items-center">
              {TESTIMONIALS.map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => setActive(i)}
                  className="flex items-center justify-center p-2"
                  aria-label={`View testimonial ${i + 1}`}
                >
                  <span className={`h-1.5 rounded-full transition-all block ${i === active ? `w-6 ${COLOR_CLASSES[item.accent].solid}` : 'w-1.5 bg-line dark:bg-dark-line'}`} />
                </button>
              ))}
            </div>
            <button
              onClick={() => setActive((p) => (p + 1) % TESTIMONIALS.length)}
              className="w-9 h-9 rounded-full border border-line dark:border-dark-line flex items-center justify-center"
              aria-label="Next testimonial"
            >
              <ChevronRight size={16} className="text-ink-muted" />
            </button>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Testimonials;
